import { Button } from '@/components/ui/button'
import { getFormLayoutSettings, getFormPlayerStyle } from '@/lib/form-theme'
import { estimateMinutes } from '@/lib/utils'
import type { PublicForm } from '@/types/form'
import { motion } from 'motion/react'
import { BrandBanner, BrandChrome } from './BrandChrome'

export function WelcomeScreen({
  form,
  onStart,
  previewMode = false,
}: {
  form: PublicForm
  onStart: () => void
  previewMode?: boolean
}) {
  const layout = getFormLayoutSettings(form.settings)
  const coverUrl = form.welcomeScreen.coverUrl

  return (
    <div
      className={
        previewMode
          ? 'form-player-theme relative grid h-full min-h-full w-full p-6'
          : 'form-player-theme relative grid min-h-screen w-full p-6'
      }
      style={getFormPlayerStyle(form.themeConfig, form.settings)}
    >
      <BrandChrome form={form} contained={previewMode} />
      <div
        className="mx-auto w-full"
        style={{
          alignSelf: layout.verticalAlign === 'top' ? 'start' : 'center',
          justifySelf:
            layout.questionAlign === 'left'
              ? 'start'
              : layout.questionAlign === 'right'
                ? 'end'
                : 'center',
          maxWidth: 'var(--form-max-width, 720px)',
          paddingBlock: layout.verticalAlign === 'top' ? 'var(--form-page-padding-block,4rem)' : 0,
          textAlign: layout.questionAlign,
        }}
      >
        <BrandBanner form={form} surface="welcome" />
        {coverUrl && (
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 w-full overflow-hidden border border-white/20 bg-white/10 shadow-sm"
            style={{
              borderRadius: form.welcomeScreen.coverRadius ?? 16,
              height: form.welcomeScreen.coverHeight
                ? `${form.welcomeScreen.coverHeight}px`
                : 'clamp(180px, 30vh, 360px)',
            }}
          >
            <img
              src={coverUrl}
              alt={form.welcomeScreen.coverAlt ?? form.welcomeScreen.title}
              className="size-full"
              style={{
                objectFit: form.welcomeScreen.coverFit ?? 'cover',
                objectPosition: `${form.welcomeScreen.coverFocalX ?? 50}% ${
                  form.welcomeScreen.coverFocalY ?? 50
                }%`,
                transform: `scale(${form.welcomeScreen.coverZoom ?? 1})`,
                transformOrigin: `${form.welcomeScreen.coverFocalX ?? 50}% ${
                  form.welcomeScreen.coverFocalY ?? 50
                }%`,
              }}
            />
          </motion.div>
        )}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-balance font-semibold tracking-normal"
          style={{
            color: 'var(--form-text,#0F172A)',
            fontFamily: 'var(--form-heading-font,var(--form-font,Geist))',
            fontSize: 'var(--form-question-size,2.25rem)',
            lineHeight: 'calc(var(--form-line-height,1.55) - 0.28)',
          }}
        >
          {form.welcomeScreen.title}
        </motion.h1>
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.08 }}
          className="mt-5 max-w-2xl"
          style={{
            color: 'var(--form-answer,#475569)',
            fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            fontSize: 'var(--form-body-size,1.125rem)',
            lineHeight: 'var(--form-line-height,1.55)',
            marginInline:
              layout.questionAlign === 'center'
                ? 'auto'
                : layout.questionAlign === 'right'
                  ? 'auto 0'
                  : '0 auto',
          }}
        >
          {form.welcomeScreen.description}
        </motion.p>
        <div
          className="mt-4"
          style={{
            color: 'var(--form-answer,#64748B)',
            fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            fontSize: 'var(--form-meta-size,0.9375rem)',
          }}
        >
          預計 {estimateMinutes(form.questions.length)} 分鐘完成
        </div>
        <Button
          className="mt-8 min-w-40 shadow-lg"
          size="lg"
          onClick={onStart}
          style={{
            background: 'var(--form-button-a11y,var(--form-button,#6366F1))',
            borderRadius: 'var(--form-radius,8px)',
            color: 'var(--form-button-text,#FFFFFF)',
          }}
        >
          開始
        </Button>
      </div>
    </div>
  )
}
